import { useSelectedPrimaryImage } from '~/hooks/image/useSelectedImages';
import { useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { RequestParameters } from '~/backend/backend-client';
import { getDatasetEnvironmentRegistry } from '~/engine/scene/datasetEnvironmentRegistryInstance';
import { useSessionOrNull } from '~/contexts/SessionContext';
import { ensureStructureSetsCurrent } from '~/hooks/ensureStructureSetsCurrent';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import {
    pseudonymizeState,
    workflowModalState,
    WorkflowTypes,
} from '~/state/atoms/viewerAtoms';
import { handleError } from '~/utils/errors';
import { NewJobData, useJobManagement } from '../../useJobManagement';
import useAnonymizer from '../useAnonymizer';
import { useDataBackend, useSessionID } from '../useBackend';
import { isSessionDerivedStructureSet } from './sessionDerivedStructureSet';
import { isDerivedDose } from '~/domain/dose/derivedDose';
import { useCurrentDose } from '~/hooks/dose/useCurrentDose';

/**
 * A React hook for creating a data upload task.
 * @returns {Object} - An object containing the start function and submission state.
 */
export function useDataUpload() {
    const dataClient = useDataBackend();
    const { anonymizeBlobs } = useAnonymizer();
    const primaryImage = useSelectedPrimaryImage();
    const currentStructureSet = useRecoilValue(currentStructureSetState);
    const currentDoseMatrix = useCurrentDose();
    const { sessionID, resetSessionID } = useSessionID();
    const pseudonymize = useRecoilValue(pseudonymizeState);
    const setWorkflowModalState = useSetRecoilState(workflowModalState);
    const { addJob } = useJobManagement();
    const session = useSessionOrNull();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const startUpload = async (action: string) => {
        if (isSubmitting) {
            return;
        }

        setIsSubmitting(true);
        resetSessionID();
        try {
            const blobs = [];
            if (primaryImage) {
                blobs.push(...Array.from(primaryImage.getSlicesAsDicomBlobs()));
            }

            if (currentStructureSet) {
                if (
                    !ensureStructureSetsCurrent(
                        getDatasetEnvironmentRegistry(),
                        [currentStructureSet],
                        'Upload'
                    )
                ) {
                    return;
                }
                if (!session) {
                    throw new Error(
                        'No active patient session for data upload.'
                    );
                }
                if (
                    !isSessionDerivedStructureSet(
                        session,
                        currentStructureSet.dicomUid
                    )
                ) {
                    blobs.push(
                        session.structureSetExportService.exportBlob(
                            currentStructureSet.dicomUid
                        )
                    );
                }
            }

            // A derived dose exists only in this session and never leaves it.
            if (currentDoseMatrix && !isDerivedDose(currentDoseMatrix)) {
                blobs.push(currentDoseMatrix.dicomDose.getAsDicomBlob());
            }

            const blobsToUpload = pseudonymize
                ? await anonymizeBlobs('data', blobs)
                : blobs;

            const backendParameters: RequestParameters = { action };

            if (blobsToUpload && blobsToUpload.length > 0) {
                const newJobDetails: NewJobData = {
                    id: sessionID,
                    name: 'Data upload',
                    totalItems: blobsToUpload.length,
                    type: WorkflowTypes.DATA_UPLOAD,
                    client: dataClient,
                    payload: {
                        filesToUpload: blobsToUpload.map((file) => ({ file })),
                        backendParameters,
                        pseudonymized: pseudonymize,
                    },
                };

                addJob(newJobDetails);
                setWorkflowModalState((state) => ({
                    ...state,
                    open: false,
                }));
            } else {
                throw new Error('Anonymization returned empty results.');
            }
        } catch (error) {
            handleError(error, 'An error occurred during file processing:');
        } finally {
            setIsSubmitting(false);
        }
    };

    return { startUpload, isSubmitting };
}
